/**
 * Guards a store action against out-of-order async resolutions clobbering
 * newer state.
 *
 * A page often fires overlapping fetches for the same resource — e.g. an
 * immediate mount fetch plus a debounced "apply filters" fetch that also
 * runs on mount. Without sequencing, if an earlier request rejects *after* a
 * later request has already resolved successfully, the earlier request's
 * error handler overwrites the good state and leaves a stale error banner
 * stuck over a populated view (issue #1313).
 *
 * Capture a token with `begin()` before the `await`, then bail out on
 * resolution when `isCurrent(token)` is false — a newer request has
 * superseded this one, so its result (success *or* error) must be discarded.
 *
 *   const leadsFetch = createRequestSequencer()
 *
 *   fetchLeads: async () => {
 *     const token = leadsFetch.begin()
 *     try {
 *       const data = await api.get(...)
 *       if (!leadsFetch.isCurrent(token)) return
 *       set({ leads: data, error: null })
 *     } catch (e) {
 *       if (!leadsFetch.isCurrent(token)) return
 *       set({ error: message(e) })
 *     }
 *   }
 */
export function createRequestSequencer() {
  let counter = 0
  return {
    /** Claim the next request slot; returns a token to check on resolution. */
    begin(): number {
      counter += 1
      return counter
    },
    /** True only if `token` is still the most recently begun request. */
    isCurrent(token: number): boolean {
      return token === counter
    },
  }
}
